import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import {
  Copy, Gift, Users, UserCheck, MessageCircle, Mail, Share2, Link2,
  CheckCircle2, Clock, Sparkles, Calendar,
} from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import LoadingSpinner from '../../components/LoadingSpinner';
import EmptyState from '../../components/EmptyState';
import * as refApi from '../../api/referrals.api';
import { errorMessage } from '../../api/client';

// Customer-facing referral dashboard.
// Wallet balance + wallet ledger have been removed per product direction;
// users see their code, share link, friendly stats, and activity timeline only.

const STATUS = {
  VISITED:   { label: 'Visited link', tone: 'neutral', icon: Link2 },
  SIGNED_UP: { label: 'Joined',       tone: 'success', icon: Sparkles },
  BOOKED:    { label: 'Booked',       tone: 'success', icon: Calendar },
  REWARDED:  { label: 'Completed',    tone: 'success', icon: CheckCircle2 },
  INVALID:   { label: 'Not counted',  tone: 'neutral', icon: Clock },
};

function statusFor(value) {
  return STATUS[value] || { label: value, tone: 'neutral', icon: Clock };
}

function timeAgo(iso) {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.floor(diff / 60_000);
  if (min < 1) return 'Just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const d = Math.floor(hr / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

function initials(text) {
  const t = String(text || '').trim();
  if (!t) return '–';
  const parts = t.split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase()).join('');
}

function Referrals() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const me = await refApi.myReferrals();
        if (!cancelled) setData(me);
      } catch (e) {
        toast.error(errorMessage(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  function copy(text, label = 'Copied') {
    navigator.clipboard?.writeText(text).then(
      () => toast.success(label),
      () => toast.error('Copy failed'),
    );
  }

  async function nativeShare(link, discount, currency) {
    if (!navigator.share) {
      copy(link, 'Link copied');
      return;
    }
    try {
      await navigator.share({
        title: 'Join me on MyDr.Ai',
        text: `Use my link and get ${currency} ${discount} off your first booking.`,
        url: link,
      });
    } catch {
      // user cancelled — no toast
    }
  }

  if (loading) return <LoadingSpinner />;
  if (!data) return <PageHeader title="Invite friends" subtitle="Could not load referral data." />;

  const { code, link, totals, referrals, config } = data;
  const discountLine = `Get ${config.currency} ${config.referredDiscount} off your first booking on MyDr.Ai — use my link: ${link}`;
  const waUrl = `https://wa.me/?text=${encodeURIComponent(discountLine)}`;
  const mailUrl = `mailto:?subject=${encodeURIComponent('Try MyDr.Ai')}&body=${encodeURIComponent(discountLine)}`;

  return (
    <>
      <PageHeader
        title="Invite friends"
        subtitle={`Share your link — your friends get ${config.currency} ${config.referredDiscount} off their first booking.`}
      />

      {/* === Share card === */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div
          style={{
            padding: 'var(--s-5)',
            background: 'linear-gradient(135deg, var(--brand-50) 0%, #fff 60%)',
            borderBottom: '1px solid var(--border)',
          }}
        >
          <div className="row" style={{ gap: 12, alignItems: 'center', marginBottom: 'var(--s-3)' }}>
            <div
              style={{
                width: 40, height: 40, borderRadius: 10,
                background: 'var(--brand-700)', color: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <Gift size={20} />
            </div>
            <div>
              <div className="bold" style={{ fontSize: 16 }}>Your invite code</div>
              <div className="muted text-sm">Share it any way you like.</div>
            </div>
          </div>

          <div
            className="row"
            style={{
              gap: 12, alignItems: 'center', flexWrap: 'wrap',
              padding: 'var(--s-3)',
              background: '#fff',
              border: '1px dashed var(--brand-200, #99e1ee)',
              borderRadius: 10,
            }}
          >
            <code
              style={{
                fontSize: 22,
                fontWeight: 700,
                letterSpacing: 2,
                padding: '4px 10px',
                color: 'var(--brand-700)',
              }}
            >
              {code}
            </code>
            <button className="btn btn-outline" onClick={() => copy(code, 'Code copied')}>
              <Copy size={14} /> Copy code
            </button>
            <button className="btn btn-primary" onClick={() => copy(link, 'Link copied')}>
              <Link2 size={14} /> Copy link
            </button>
          </div>
        </div>

        <div className="row" style={{ gap: 8, padding: 'var(--s-4)', flexWrap: 'wrap' }}>
          <a
            href={waUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-outline"
            style={{ color: '#075E54', borderColor: '#075E54' }}
          >
            <MessageCircle size={14} /> Share on WhatsApp
          </a>
          <a href={mailUrl} className="btn btn-outline">
            <Mail size={14} /> Email
          </a>
          <button
            type="button"
            className="btn btn-outline"
            onClick={() => nativeShare(link, config.referredDiscount, config.currency)}
          >
            <Share2 size={14} /> More…
          </button>
        </div>
      </div>

      {/* === Stats === */}
      <div className="grid grid-2" style={{ gap: 'var(--s-3)', marginTop: 'var(--s-4)' }}>
        <Stat
          icon={Users}
          tone="brand"
          label="Link visits"
          value={totals.total}
          hint="Times your link was opened."
        />
        <Stat
          icon={UserCheck}
          tone="emerald"
          label="Friends signed up"
          value={totals.conversions}
          hint="People who joined after using your link."
        />
      </div>

      {/* === Activity timeline === */}
      <div className="card" style={{ marginTop: 'var(--s-4)' }}>
        <div className="card-title">
          <h3>Activity</h3>
          <span className="muted text-sm">{referrals.length} {referrals.length === 1 ? 'event' : 'events'}</span>
        </div>

        {referrals.length === 0 ? (
          <EmptyState
            title="No activity yet"
            description="Share your link with a friend to get started. You'll see every visit and sign-up here."
          />
        ) : (
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {referrals.map((r) => {
              const s = statusFor(r.status);
              const Icon = s.icon;
              const display = r.referredUser?.name
                || r.referredUser?.email
                || 'New visitor';
              const isVisitor = !r.referredUser;
              return (
                <li
                  key={r._id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '12px 0',
                    borderBottom: '1px solid var(--border)',
                  }}
                >
                  <div
                    style={{
                      width: 36, height: 36, borderRadius: '50%',
                      background: isVisitor ? 'var(--ink-100)' : 'var(--brand-50, #f0f9ff)',
                      color: isVisitor ? 'var(--ink-500)' : 'var(--brand-700)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontWeight: 700, fontSize: 13, flexShrink: 0,
                    }}
                  >
                    {initials(display)}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="bold" style={{ fontSize: 14 }}>
                      {isVisitor ? <span className="muted">New visitor</span> : display}
                    </div>
                    <div className="muted text-xs">{timeAgo(r.createdAt)}</div>
                  </div>
                  <span className={`badge ${s.tone}`}>
                    <Icon size={12} /> {s.label}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </>
  );
}

function Stat({ icon: Icon, tone, label, value, hint }) {
  return (
    <div className="card">
      <div className="row" style={{ gap: 10, alignItems: 'center' }}>
        <div
          className={`stat-icon ${tone || ''}`}
          style={{
            width: 36, height: 36, borderRadius: 10,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'var(--brand-50, #f0f9ff)',
            color: 'var(--brand-700)',
            flexShrink: 0,
          }}
        >
          <Icon size={18} />
        </div>
        <div style={{ flex: 1 }}>
          <div className="muted text-xs" style={{ textTransform: 'uppercase', letterSpacing: '0.1em' }}>
            {label}
          </div>
          <div className="bold" style={{ fontSize: 24, lineHeight: 1.1, marginTop: 2 }}>{value}</div>
        </div>
      </div>
      {hint && (
        <div className="muted text-xs" style={{ marginTop: 10 }}>{hint}</div>
      )}
    </div>
  );
}

export default Referrals;
